import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { enrichMealNutrients } from '@/hooks/useMeals';
import type {
  Favorite,
  FavoriteIngredient,
  FavoriteWithIngredients,
  LogMealFromFavoriteParams,
  LogMealFromFavoriteResult,
  CreateRecipeParams,
  MealType,
} from '@/types';

// ============================================
// ХУК: СПИСОК ИЗБРАННОГО
// ============================================

type FavoriteFilter = 'all' | 'recipes' | 'products';

/**
 * Хук для получения списка избранного юзера.
 * Автоматически загружает при mount, можно фильтровать.
 *
 * @example
 * const { favorites, loading, refetch } = useUserFavorites('recipes');
 */
export function useUserFavorites(filter: FavoriteFilter = 'all', limit = 50) {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc('get_user_favorites', {
      p_filter: filter,
      p_limit: limit,
    });

    if (rpcError) {
      console.error('[useUserFavorites] error:', rpcError);
      setError(rpcError.message);
      setFavorites([]);
    } else {
      setFavorites((data ?? []) as Favorite[]);
    }

    setLoading(false);
  }, [filter, limit]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { favorites, loading, error, refetch };
}

// ============================================
// ДОБАВЛЕНИЕ В ДНЕВНИК
// ============================================

/**
 * Добавляет рецепт или продукт в дневник как один meal с одним meal_item.
 * Универсальная функция — принимает portions ИЛИ grams.
 *
 * Для рецептов: используется portions (можно дробное 0.5, 1, 1.5)
 *   ИЛИ grams (если нужна точность по граммам).
 * Для продуктов: используется только grams.
 *
 * @example
 * // Рецепт борща, 1 порция, обед сегодня
 * await logMealFromFavorite({
 *   favorite_id: 'uuid',
 *   portions: 1,
 *   meal_type: 'lunch',
 * });
 *
 * // Куриная грудка, 200г, ужин
 * await logMealFromFavorite({
 *   favorite_id: 'uuid',
 *   grams: 200,
 *   meal_type: 'dinner',
 * });
 */
export async function logMealFromFavorite(
  params: LogMealFromFavoriteParams
): Promise<LogMealFromFavoriteResult> {
  const { favorite_id, portions, grams, meal_type, consumed_at } = params;

  if (!favorite_id) {
    return { success: false, error: 'missing_favorite_id' };
  }

  if (portions === undefined && grams === undefined) {
    return { success: false, error: 'missing_portions_or_grams' };
  }

  const { data, error } = await supabase.rpc('log_meal_from_favorite', {
    p_favorite_id: favorite_id,
    p_portions: portions ?? null,
    p_grams: grams ?? null,
    p_meal_type: meal_type,
    p_consumed_at: consumed_at ? consumed_at.toISOString() : new Date().toISOString(),
  });

  if (error) {
    console.error('[logMealFromFavorite] error:', error);
    return { success: false, error: error.message } as LogMealFromFavoriteResult;
  }

  // Приём создаётся целиком серверной RPC, мимо клиентского createMeal —
  // поэтому обогащение микронутриентами надо дозвать руками, иначе продукт
  // попадёт в дневник с нулями по витаминам и минералам.
  const logged = (data ?? { success: false, error: 'no_data' }) as LogMealFromFavoriteResult;
  if (logged.meal_id) {
    enrichMealNutrients(logged.meal_id).catch((e) =>
      console.error('[logMealFromFavorite] enrich failed:', e),
    );
  }

  return logged;
}

// ============================================
// ПОЛУЧИТЬ РЕЦЕПТ С ИНГРЕДИЕНТАМИ
// ============================================

/**
 * Получает полный рецепт со списком ингредиентов.
 * Используется при открытии экрана редактирования.
 *
 * @example
 * const result = await getFavoriteWithIngredients('uuid');
 * if (result) {
 *   console.log(result.favorite);
 *   console.log(result.ingredients);
 * }
 */
export async function getFavoriteWithIngredients(
  favorite_id: string
): Promise<FavoriteWithIngredients | null> {
  if (!favorite_id) return null;

  const { data, error } = await supabase.rpc('get_favorite_with_ingredients', {
    p_favorite_id: favorite_id,
  });

  if (error) {
    console.error('[getFavoriteWithIngredients] error:', error);
    return null;
  }

  if (!data?.success) {
    console.warn('[getFavoriteWithIngredients] not found:', data?.error);
    return null;
  }

  return {
    favorite: data.favorite as Favorite,
    ingredients: (data.ingredients ?? []) as FavoriteIngredient[],
  };
}

// ============================================
// УДАЛЕНИЕ ИЗБРАННОГО
// ============================================

/**
 * Безопасно удаляет рецепт/продукт из избранного.
 * Записи в дневнике сохраняются (favorite_id становится NULL).
 *
 * @returns { success, deleted, history_records_kept }
 */
export async function deleteFavorite(favorite_id: string): Promise<{
  success: boolean;
  deleted?: boolean;
  history_records_kept?: number;
  error?: string;
}> {
  if (!favorite_id) {
    return { success: false, error: 'missing_id' };
  }

  const { data, error } = await supabase.rpc('delete_favorite_safely', {
    p_favorite_id: favorite_id,
  });

  if (error) {
    console.error('[deleteFavorite] error:', error);
    return { success: false, error: error.message };
  }

  return (data ?? { success: false }) as any;
}

// ============================================
// СОЗДАНИЕ РЕЦЕПТА
// ============================================

/**
 * Создаёт новый рецепт с ингредиентами.
 * КБЖУ на 1 порцию считается автоматически: сумма ингредиентов / total_portions.
 *
 * Выполняется в 2 шага (без транзакции):
 * 1. Создаётся favorite (рецепт)
 * 2. Создаются ингредиенты для него
 *
 * Если 2-й шаг провалится — рецепт пустой, юзер увидит и сможет редактировать.
 *
 * @example
 * const recipe = await createRecipe({
 *   name: 'Мамин борщ',
 *   emoji: '🍲',
 *   total_portions: 6,
 *   ingredients: [
 *     { name: 'говядина', grams: 500, calories: 950, protein_g: 100, fat_g: 60, carbs_g: 0 },
 *     ...
 *   ],
 * });
 */
export async function createRecipe(params: CreateRecipeParams): Promise<{
  success: boolean;
  recipe?: Favorite;
  error?: string;
}> {
  const {
    name,
    emoji = '🍽',
    category = null,
    total_portions,
    portion_label = 'порция',
    portion_weight_g,
    notes,
    ingredients,
  } = params;

  // Валидация
  if (!name?.trim()) {
    return { success: false, error: 'missing_name' };
  }
  if (total_portions <= 0) {
    return { success: false, error: 'invalid_portions' };
  }
  if (!ingredients?.length) {
    return { success: false, error: 'no_ingredients' };
  }

  // Считаем общий КБЖУ рецепта
  const totals = ingredients.reduce(
    (acc, ing) => ({
      calories: acc.calories + (Number(ing.calories) || 0),
      protein_g: acc.protein_g + (Number(ing.protein_g) || 0),
      fat_g: acc.fat_g + (Number(ing.fat_g) || 0),
      carbs_g: acc.carbs_g + (Number(ing.carbs_g) || 0),
      grams: acc.grams + (Number(ing.grams) || 0),
    }),
    { calories: 0, protein_g: 0, fat_g: 0, carbs_g: 0, grams: 0 }
  );

  // КБЖУ на 1 порцию
  const caloriesPerPortion = Math.round(totals.calories / total_portions);
  const proteinPerPortion = +(totals.protein_g / total_portions).toFixed(1);
  const fatPerPortion = +(totals.fat_g / total_portions).toFixed(1);
  const carbsPerPortion = +(totals.carbs_g / total_portions).toFixed(1);

  // Вес одной порции (если не задан — считаем по сумме ингредиентов)
  const portionWeight = portion_weight_g ?? Math.round(totals.grams / total_portions);

  // Получаем юзера
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return { success: false, error: 'not_authenticated' };
  }

  // Шаг 1: создаём рецепт
  const { data: recipe, error: recipeError } = await supabase
    .from('favorites')
    .insert({
      user_id: user.id,
      name: name.trim(),
      emoji,
      is_recipe: true,
      category,

      // Старые поля для совместимости — для рецепта по 100г = на 100г от целого
      calories_per_100g: totals.grams > 0 ? Math.round((totals.calories / totals.grams) * 100) : 0,
      protein_per_100g: totals.grams > 0 ? +((totals.protein_g / totals.grams) * 100).toFixed(1) : 0,
      fat_per_100g: totals.grams > 0 ? +((totals.fat_g / totals.grams) * 100).toFixed(1) : 0,
      carbs_per_100g: totals.grams > 0 ? +((totals.carbs_g / totals.grams) * 100).toFixed(1) : 0,

      // Новые поля для рецепта
      total_portions,
      portion_label,
      portion_weight_g: portionWeight,
      calories_per_portion: caloriesPerPortion,
      protein_per_portion: proteinPerPortion,
      fat_per_portion: fatPerPortion,
      carbs_per_portion: carbsPerPortion,
      notes,
    })
    .select()
    .single();

  if (recipeError || !recipe) {
    console.error('[createRecipe] failed to create recipe:', recipeError);
    return { success: false, error: recipeError?.message ?? 'create_failed' };
  }

  // Шаг 2: создаём ингредиенты
  const { error: ingredientsError } = await supabase.from('favorite_ingredients').insert(
    ingredients.map((ing, idx) => ({
      favorite_id: recipe.id,
      name: ing.name.trim(),
      grams: ing.grams,
      calories: Math.round(ing.calories),
      protein_g: +Number(ing.protein_g).toFixed(1),
      fat_g: +Number(ing.fat_g).toFixed(1),
      carbs_g: +Number(ing.carbs_g).toFixed(1),
      position: idx,
    }))
  );

  if (ingredientsError) {
    console.error('[createRecipe] failed to create ingredients:', ingredientsError);
    // Рецепт создан, но ингредиентов нет.
    // Пользователь увидит пустой рецепт и сможет его удалить или отредактировать.
    return {
      success: false,
      recipe: recipe as Favorite,
      error: 'recipe_created_but_no_ingredients: ' + ingredientsError.message,
    };
  }

  return { success: true, recipe: recipe as Favorite };
}

// ============================================
// УПРОЩЁННОЕ ДОБАВЛЕНИЕ ПРОДУКТА В ИЗБРАННОЕ
// ============================================

/**
 * Добавляет простой продукт в избранное.
 * Используется для звёздочки на блюде в Дневнике.
 *
 * @example
 * await addProductToFavorites({
 *   name: 'Куриная грудка',
 *   calories_per_100g: 165,
 *   protein_per_100g: 31,
 *   fat_per_100g: 3.6,
 *   carbs_per_100g: 0,
 * });
 */
export async function addProductToFavorites(params: {
  name: string;
  calories_per_100g: number;
  protein_per_100g?: number;
  fat_per_100g?: number;
  carbs_per_100g?: number;
  emoji?: string;
}): Promise<{ success: boolean; favorite?: Favorite; error?: string }> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return { success: false, error: 'not_authenticated' };
  }

  const { data, error } = await supabase
    .from('favorites')
    .insert({
      user_id: user.id,
      name: params.name.trim(),
      emoji: params.emoji ?? '🍽',
      is_recipe: false,
      calories_per_100g: Math.round(params.calories_per_100g),
      protein_per_100g: +(params.protein_per_100g ?? 0).toFixed(1),
      fat_per_100g: +(params.fat_per_100g ?? 0).toFixed(1),
      carbs_per_100g: +(params.carbs_per_100g ?? 0).toFixed(1),
    })
    .select()
    .single();

  if (error) {
    console.error('[addProductToFavorites] error:', error);
    return { success: false, error: error.message };
  }

  return { success: true, favorite: data as Favorite };
}

// ============================================
// ОБНОВЛЕНИЕ БАЗОВЫХ ПОЛЕЙ РЕЦЕПТА
// ============================================

/**
 * Обновляет основные поля рецепта (имя, эмодзи, категория, заметки).
 * Не трогает ингредиенты и КБЖУ.
 *
 * Для обновления ингредиентов — используй updateRecipeIngredients.
 */
export async function updateFavoriteBasic(
  favorite_id: string,
  updates: Partial<Pick<Favorite, 'name' | 'emoji' | 'category' | 'notes' | 'portion_label'>>
): Promise<{ success: boolean; error?: string }> {
  if (!favorite_id) {
    return { success: false, error: 'missing_id' };
  }

  const { error } = await supabase.from('favorites').update(updates).eq('id', favorite_id);

  if (error) {
    console.error('[updateFavoriteBasic] error:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================
// ОБНОВЛЕНИЕ ИНГРЕДИЕНТОВ РЕЦЕПТА
// ============================================

/**
 * Полностью пересоздаёт ингредиенты рецепта.
 * Также пересчитывает КБЖУ на 1 порцию.
 *
 * Логика: удаляем все ингредиенты → вставляем новые → пересчёт.
 * (Это проще чем "diff" и для маленьких списков ингредиентов нормально.)
 */
export async function updateRecipeIngredients(
  favorite_id: string,
  total_portions: number,
  ingredients: CreateRecipeParams['ingredients']
): Promise<{ success: boolean; error?: string }> {
  if (!favorite_id) {
    return { success: false, error: 'missing_id' };
  }
  if (total_portions <= 0) {
    return { success: false, error: 'invalid_portions' };
  }
  if (!ingredients?.length) {
    return { success: false, error: 'no_ingredients' };
  }

  // Считаем новые КБЖУ
  const totals = ingredients.reduce(
    (acc, ing) => ({
      calories: acc.calories + (Number(ing.calories) || 0),
      protein_g: acc.protein_g + (Number(ing.protein_g) || 0),
      fat_g: acc.fat_g + (Number(ing.fat_g) || 0),
      carbs_g: acc.carbs_g + (Number(ing.carbs_g) || 0),
      grams: acc.grams + (Number(ing.grams) || 0),
    }),
    { calories: 0, protein_g: 0, fat_g: 0, carbs_g: 0, grams: 0 }
  );

  const portionWeight = Math.round(totals.grams / total_portions);

  // 1. Обновляем рецепт (общий КБЖУ + размер порции)
  const { error: updateError } = await supabase
    .from('favorites')
    .update({
      total_portions,
      portion_weight_g: portionWeight,
      calories_per_portion: Math.round(totals.calories / total_portions),
      protein_per_portion: +(totals.protein_g / total_portions).toFixed(1),
      fat_per_portion: +(totals.fat_g / total_portions).toFixed(1),
      carbs_per_portion: +(totals.carbs_g / total_portions).toFixed(1),

      // На 100г — для совместимости
      calories_per_100g: totals.grams > 0 ? Math.round((totals.calories / totals.grams) * 100) : 0,
      protein_per_100g:
        totals.grams > 0 ? +((totals.protein_g / totals.grams) * 100).toFixed(1) : 0,
      fat_per_100g: totals.grams > 0 ? +((totals.fat_g / totals.grams) * 100).toFixed(1) : 0,
      carbs_per_100g: totals.grams > 0 ? +((totals.carbs_g / totals.grams) * 100).toFixed(1) : 0,
    })
    .eq('id', favorite_id);

  if (updateError) {
    console.error('[updateRecipeIngredients] failed update recipe:', updateError);
    return { success: false, error: updateError.message };
  }

  // 2. Удаляем старые ингредиенты
  const { error: deleteError } = await supabase
    .from('favorite_ingredients')
    .delete()
    .eq('favorite_id', favorite_id);

  if (deleteError) {
    console.error('[updateRecipeIngredients] failed delete old:', deleteError);
    return { success: false, error: deleteError.message };
  }

  // 3. Вставляем новые
  const { error: insertError } = await supabase.from('favorite_ingredients').insert(
    ingredients.map((ing, idx) => ({
      favorite_id,
      name: ing.name.trim(),
      grams: ing.grams,
      calories: Math.round(ing.calories),
      protein_g: +Number(ing.protein_g).toFixed(1),
      fat_g: +Number(ing.fat_g).toFixed(1),
      carbs_g: +Number(ing.carbs_g).toFixed(1),
      position: idx,
    }))
  );

  if (insertError) {
    console.error('[updateRecipeIngredients] failed insert new:', insertError);
    return { success: false, error: insertError.message };
  }

  return { success: true };
}
